import useSWR from 'swr'
import {
  PopulationFilter,
  WorkflowRun,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import { populationsApi } from '@/lib/api'

export interface RunSegmentQuote {
  /** The run's population filter, or null for a full-audience run. */
  filter: PopulationFilter | null
  /**
   * How many people the filter matches right now. Null when there is no
   * filter, while counting, or when the count could not be read: the quote
   * then stays the panel.
   */
  matchingCount: number | null
  isCounting: boolean
}

function audienceIdOf(
  run: WorkflowRun | null,
  template: WorkflowTemplate | null,
): string | null {
  if (!run) {
    return null
  }
  const key =
    template?.inputs.find((spec) => spec.type === 'audience')?.key ?? 'audience'
  const raw = run.inputs?.[key]
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null
  }
  return raw.trim()
}

/**
 * The live "N match" count for a filtered run, from the same filter-preview
 * endpoint the audience filter modal and its ask-AI chips count with, so the
 * gate and the modal can never disagree about a filter.
 */
export function useRunSegmentQuote(
  run: WorkflowRun | null,
  template: WorkflowTemplate | null,
): RunSegmentQuote {
  const filter = run?.populationFilter ?? null
  const audienceId = filter ? audienceIdOf(run, template) : null
  const filterKey = filter ? JSON.stringify(filter) : null

  const { data, error, isLoading } = useSWR(
    audienceId && filterKey
      ? ['run-segment-count', audienceId, filterKey]
      : null,
    () => populationsApi.previewFilter(audienceId!, filter),
    { revalidateOnFocus: false },
  )

  const matchingCount =
    !error && data && typeof data.matchingCount === 'number'
      ? data.matchingCount
      : null

  return {
    filter,
    matchingCount: filter ? matchingCount : null,
    isCounting: Boolean(filter) && isLoading,
  }
}
